import type {
  DownloadAssetType as CanonicalDownloadAssetType,
  TrackingEventType as CanonicalTrackingEventType,
  DownloadEventData,
  SearchEventData,
} from "@color-finder/color-finder-api/models";
import type { AxiosInstance } from "axios";
import { appendSiteMinderBypass } from "../config/axios-interceptors";
import { getFeatureFlagsFromCache } from "../hooks/use-config";

export type {
  DownloadEventData,
  SearchEventData,
} from "@color-finder/color-finder-api/models";

// Canonical definitions live in @color-finder/color-finder-api/models
// (packages/lib/color-finder-api/src/models/tracking.ts). They are re-declared
// here because CRA's Rollup-based Storybook build cannot statically trace the
// named exports through the CJS __exportStar boundary of the shared models
// package (see commit 58eb18bf). Keep both lists in sync when adding values.
export const TrackingEventType = {
  LOGIN: "login",
  LOGOUT: "logout",
  SEARCH: "search",
  DOWNLOAD: "download",
} as const;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type TrackingEventType =
  (typeof TrackingEventType)[keyof typeof TrackingEventType];

export const DownloadAssetType = {
  AXF: "axf",
  IMAGE: "image",
  RENDERING: "rendering",
  OTHER: "other",
} as const;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type DownloadAssetType =
  (typeof DownloadAssetType)[keyof typeof DownloadAssetType];

// Compile-time guard: if the webapp copies drift from the canonical API
// models (either direction), these type aliases resolve to `never` and the
// `AssertEqual` check below fails to type-check.
type AssertEqual<A, B> = [A] extends [B]
  ? [B] extends [A]
    ? true
    : never
  : never;
type _TrackingEventTypeInSync = AssertEqual<
  TrackingEventType,
  CanonicalTrackingEventType
>;
type _DownloadAssetTypeInSync = AssertEqual<
  DownloadAssetType,
  CanonicalDownloadAssetType
>;
const _syncChecks: [_TrackingEventTypeInSync, _DownloadAssetTypeInSync] = [
  true,
  true,
];
void _syncChecks;

// Consent gating is not applied at the call site: the tracking API is
// JWT-protected and policy (see commit e045c6f8) is "login status over
// consent" - authenticated users are always tracked, consent is captured
// at registration/ToS.
const postTracking = (
  axios: AxiosInstance,
  eventType: TrackingEventType,
  body?: unknown,
): void => {
  if (!getFeatureFlagsFromCache().colorTracking) return;
  axios.post(`/v0/tracking/${eventType}`, body).catch((error) => {
    console.warn(`Tracking ${eventType} failed`, error);
  });
};

/**
 * Fire-and-forget `login` event. Errors are logged and swallowed — the
 * caller should never await this or branch on its result.
 */
export const trackLogin = (axios: AxiosInstance): void =>
  postTracking(axios, TrackingEventType.LOGIN);

/**
 * Fire-and-forget `logout` event.
 *
 * Logout may be immediately followed by `window.location.reload()` (CDC
 * flow), which aborts in-flight XHR. We therefore use `fetch` with
 * `keepalive: true` so the request survives page unload. `sendBeacon` is
 * unsuitable because it can't set the `Authorization` header required by
 * the JWT-protected tracking endpoint.
 *
 * @param bearerToken - Active access token. If absent, the call is skipped
 *   (the endpoint would 401 anyway) and a debug warning is logged.
 */
export const trackLogout = (bearerToken: string | undefined): void => {
  if (!getFeatureFlagsFromCache().colorTracking) return;
  if (!bearerToken) {
    // The tracking endpoint is JWT-protected, so a request without a bearer
    // would just 401. Skip the call and surface the miss in debug builds.
    console.warn(
      `Tracking ${TrackingEventType.LOGOUT} skipped: no bearer token`,
    );
    return;
  }
  const url = appendSiteMinderBypass(
    `/v0/tracking/${TrackingEventType.LOGOUT}`,
  );
  fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${bearerToken}` },
    keepalive: true,
  }).catch((error) => {
    console.warn(`Tracking ${TrackingEventType.LOGOUT} failed`, error);
  });
};

/**
 * Fire-and-forget `search` event. Deduplication (skipping identical
 * consecutive searches) is the caller's responsibility — see
 * `useSearchTracking` in `hooks/colored-materials.ts`.
 */
export const trackSearch = (
  axios: AxiosInstance,
  eventData: SearchEventData,
): void => postTracking(axios, TrackingEventType.SEARCH, eventData);

/** Fire-and-forget `download` event. Errors are logged and swallowed. */
export const trackDownload = (
  axios: AxiosInstance,
  eventData: DownloadEventData,
): void => postTracking(axios, TrackingEventType.DOWNLOAD, eventData);

// Shape of the `digitalSample` sub-object on DownloadEventData. Duplicated
// verbatim across download dialogs before this helper existed.
type DigitalSampleForTracking = Pick<
  NonNullable<DownloadEventData["digitalSample"]>,
  "id" | "isPublished"
> & {
  materialFinish?: string;
};

interface ColoredMaterialForTracking {
  material: { shortGrade?: string };
  color: { finalColorCode?: string; preliminaryColorCode?: string };
}

/**
 * Build the `digitalSample` sub-object of a download tracking event.
 *
 * Shared by `AxFDownloadDialog` and `DigitalSampleDownloadDialog`.
 * `materialColorCode` falls back to `"UNKNOWN"` so dashboards grouping by
 * color code don't silently drop events where both the final and
 * preliminary codes are absent.
 */
export const buildDigitalSampleTrackingData = (
  material: ColoredMaterialForTracking,
  digitalSample: DigitalSampleForTracking,
): NonNullable<DownloadEventData["digitalSample"]> => ({
  id: digitalSample.id,
  materialGrade: material.material.shortGrade,
  materialColorCode:
    material.color.finalColorCode ??
    material.color.preliminaryColorCode ??
    "UNKNOWN",
  materialFinish: digitalSample.materialFinish,
  isPublished: digitalSample.isPublished,
});

/**
 * Classify a raw asset type label into a `DownloadAssetType`.
 *
 * Case-insensitive substring match with priority order
 * `axf > rendering > image > other`. `axf` wins first because some labels
 * contain both `"axf"` and `"image"` (e.g. "AxF material image") and those
 * should be classified as AXF.
 */
export const inferAssetType = (assetType: string): DownloadAssetType => {
  const normalized = assetType.toLowerCase();
  if (normalized.includes("axf")) return DownloadAssetType.AXF;
  if (normalized.includes("rendering")) return DownloadAssetType.RENDERING;
  if (normalized.includes("image")) return DownloadAssetType.IMAGE;
  return DownloadAssetType.OTHER;
};
